import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getSocialMediaCategoriesWithImages,
  getMeCustomization,
  setUserSocialCustomization,
  type GetCategoriesWithImagesResponse
} from 'common/api/UserSocialCustomization';
import Button from 'common/components/Button';
import { EDIT_PROFILE_CARD_PICKER_MODAL } from 'common/module/ui/lazy-modal/constant-types';
import buildReactDomModalModule from 'common/module/ui/lazy-modal/react-dom-modal-module';
import ReactModal from 'common/module/ui/lazy-modal/react-modal';
import {
  CloseModal,
  OnCloseParams,
  type ProfileCardPickerResult
} from 'common/module/ui/lazy-modal/types';
import { formatSocialMediaImageEndDate } from 'common/tools/formatSocialMediaImageEndDate';
import { resolveGraphQlMediaPath } from 'common/tools/network/url';
import trans from 'common/tools/translations/trans';
import {
  hadProfileCustomizationForMode,
  trackProfileCardPickerSave
} from 'common/tracking/profileCardPickerTracking';

import styles from './ProfileCardPickerModal.module.scss';
import ProfileCardPickerRollerNav from './ProfileCardPickerRollerNav';

export type ProfileCardPickerCategory = {
  id: string;
  label: string;
  internalId?: string | number;
  sponsored?: boolean;
  rank?: number | null;
};

export type ProfileCardPickerItem = {
  id: string;
  internalId: number;
  imageUrl: string;
  categoryId: string;
  endDate?: string | null;
};

export type ProfileCardPickerModalParams = {
  mode: 'avatar' | 'background';
  categories?: ProfileCardPickerCategory[];
  items?: ProfileCardPickerItem[];
  selectedId?: string;
};

export type { ProfileCardPickerResult };

function buildCategoriesAndItemsFromApi(
  _mode: 'avatar' | 'background',
  response: GetCategoriesWithImagesResponse
): { categories: ProfileCardPickerCategory[]; items: ProfileCardPickerItem[] } {
  const categories: ProfileCardPickerCategory[] = [];
  const items: ProfileCardPickerItem[] = [];
  const edges = response?.data?.socialMediaCategoryList?.edges ?? [];
  for (const edge of edges) {
    const node = edge?.node;
    if (!node?.id) continue;
    const categoryId = node.id;
    const categoryInternalId =
      node.internalId != null ? Number(node.internalId) : undefined;
    categories.push({
      id: categoryId,
      label: node.name ?? categoryId,
      internalId: categoryInternalId,
      sponsored: node.sponsored === true,
      rank: node.rank ?? null
    });
    const imageEdges = node.images?.edges ?? [];
    for (const imgEdge of imageEdges) {
      const img = imgEdge?.node;
      if (!img?.id) continue;
      const internalId = Number(img.internalId ?? 0);
      if (!internalId) continue;
      const imageUrl = img.path ? resolveGraphQlMediaPath(img.path) : '';
      items.push({
        id: img.id,
        internalId,
        imageUrl,
        categoryId,
        endDate: img.endDate ?? null
      });
    }
  }
  categories.sort((a, b) => {
    const sponsoredDiff = (b.sponsored ? 1 : 0) - (a.sponsored ? 1 : 0);
    if (sponsoredDiff !== 0) return sponsoredDiff;
    const ra = a.rank ?? Number.MAX_SAFE_INTEGER;
    const rb = b.rank ?? Number.MAX_SAFE_INTEGER;
    return ra - rb;
  });
  return { categories, items };
}

const ProfileCardPickerModal = ({
  closeModal,
  onCloseParams,
  params
}: {
  closeModal: CloseModal;
  onCloseParams: OnCloseParams;
  params: ProfileCardPickerModalParams | null | undefined;
}) => {
  const {
    categories: paramsCategories = [],
    items: paramsItems = [],
    selectedId: paramsSelectedId
  } = params ?? {};
  const mode: 'avatar' | 'background' = params?.mode ?? 'avatar';
  const [categories, setCategories] =
    useState<ProfileCardPickerCategory[]>(paramsCategories);
  const [items, setItems] = useState<ProfileCardPickerItem[]>(paramsItems);
  const [loading, setLoading] = useState(
    () => paramsCategories.length === 0 && paramsItems.length === 0
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>(
    paramsSelectedId
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | undefined>(
    paramsCategories[0]?.id ?? categories[0]?.id
  );
  const [initialSelectedId, setInitialSelectedId] = useState<
    string | undefined
  >(paramsSelectedId);
  const [hadExistingCustomization, setHadExistingCustomization] =
    useState(false);

  const alreadyChosenId = paramsSelectedId ?? initialSelectedId;

  useEffect(() => {
    if (paramsCategories.length > 0 || paramsItems.length > 0) {
      let cancelled = false;
      getMeCustomization()
        .then(res => {
          if (cancelled) return;
          setHadExistingCustomization(
            hadProfileCustomizationForMode(
              mode,
              res?.data?.me?.user?.customization
            )
          );
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const graphqlType = mode === 'avatar' ? 'AVATAR' : 'BANNER';
    Promise.all([
      getSocialMediaCategoriesWithImages(graphqlType),
      getMeCustomization()
    ])
      .then(([categoriesRes, customizationRes]) => {
        if (cancelled) return;
        const { categories: cat, items: it } = buildCategoriesAndItemsFromApi(
          mode,
          categoriesRes
        );
        setCategories(cat);
        setItems(it);

        const customization = customizationRes?.data?.me?.user?.customization;
        setHadExistingCustomization(
          hadProfileCustomizationForMode(mode, customization)
        );
        const current =
          mode === 'avatar'
            ? customization?.mainAvatar
            : customization?.mainBanner;
        const currentCategoryId =
          current?.categoryId != null ? Number(current.categoryId) : undefined;
        const currentInternalId =
          current?.internalId != null ? Number(current.internalId) : undefined;

        let appliedCategoryId = false;
        if (
          currentCategoryId != null &&
          currentInternalId != null &&
          cat.length > 0 &&
          it.length > 0
        ) {
          const matchingCategory = cat.find(
            c =>
              c.internalId != null && Number(c.internalId) === currentCategoryId
          );
          const matchingItem = it.find(
            item => item.internalId === currentInternalId
          );
          if (matchingCategory?.id) {
            setActiveCategoryId(matchingCategory.id);
            appliedCategoryId = true;
          }
          if (matchingItem?.id) {
            setSelectedId(matchingItem.id);
            setInitialSelectedId(matchingItem.id);
          }
        }
        if (!appliedCategoryId && cat[0]?.id) setActiveCategoryId(cat[0].id);
      })
      .catch(err => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, paramsCategories.length, paramsItems.length]);

  const filteredItems = useMemo(() => {
    if (!activeCategoryId) return items;
    return items.filter(item => item.categoryId === activeCategoryId);
  }, [items, activeCategoryId]);

  const selectedItem = useMemo(
    () => items.find(item => item.id === selectedId),
    [items, selectedId]
  );

  const activeCategory = useMemo(
    () => categories.find(c => c.id === activeCategoryId),
    [categories, activeCategoryId]
  );

  const rollerNavRevalidateKey = useMemo(
    () => categories.map(c => c.id).join('|'),
    [categories]
  );

  const showSponsoredDisclaimer =
    activeCategory?.sponsored === true &&
    !loading &&
    !error &&
    filteredItems.length > 0;

  const title =
    mode === 'avatar'
      ? trans('userprofile.edit-card.picker.title.avatar')
      : trans('userprofile.edit-card.picker.title.background');

  const handleValidate = useCallback(() => {
    if (!selectedItem) return;
    setSaving(true);
    const input =
      mode === 'avatar'
        ? { id_avatar: selectedItem.internalId }
        : { id_banner: selectedItem.internalId };
    setUserSocialCustomization(input)
      .then(res => {
        const customization =
          res?.data?.setUserSocialCustomization?.customization;
        const path =
          mode === 'avatar'
            ? customization?.mainAvatar?.path
            : customization?.mainBanner?.path;
        const imageUrl = path
          ? resolveGraphQlMediaPath(path) || selectedItem.imageUrl
          : selectedItem.imageUrl;
        trackProfileCardPickerSave(
          mode === 'avatar' ? 'avatar' : 'background',
          hadExistingCustomization
        );
        const result: ProfileCardPickerResult = {
          mode,
          selectedId: selectedItem.id,
          imageUrl
        };
        onCloseParams?.(result);
        closeModal();
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        setSaving(false);
      });
  }, [mode, selectedItem, onCloseParams, closeModal, hadExistingCustomization]);

  const handleCancel = () => {
    closeModal();
  };

  const isCircle = mode === 'avatar';

  return (
    <ReactModal
      size="medium"
      title={title}
      className={
        isCircle
          ? styles.pickerModal
          : `${styles.pickerModal} ${styles.pickerModalBackground}`
      }
    >
      {categories.length > 0 && (
        <nav
          className={styles.rollerNav}
          role="navigation"
          aria-label={trans('userprofile.edit-card.picker.categories.aria')}
        >
          <ProfileCardPickerRollerNav
            className="roller-arrow-title roller-entity-page-menu"
            offset={20}
            revalidateKey={rollerNavRevalidateKey}
          >
            {categories.map(cat => (
              <button
                key={cat.id}
                type="button"
                className={classnames('roller-item', {
                  current: activeCategoryId === cat.id
                })}
                onClick={() => setActiveCategoryId(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </ProfileCardPickerRollerNav>
        </nav>
      )}
      <div
        className={classnames(
          'gd gd-gap-15',
          loading || error || filteredItems.length === 0
            ? 'gd-xs-1'
            : isCircle
              ? 'gd-xs-3 gd-s-4'
              : 'gd-xs-1 gd-s-2',
          isCircle
            ? styles.pickerItems
            : [styles.pickerItems, styles.pickerItemsBackground]
        )}
      >
        {loading ? (
          <div className={styles.empty}>
            {trans('userspace.edit-card.picker.loading')}
          </div>
        ) : error ? (
          <div className={styles.empty}>{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className={styles.empty}>
            {trans('userprofile.edit-card.picker.empty')}
          </div>
        ) : (
          filteredItems.map(item => {
            const isAlreadyChosen = item.id === alreadyChosenId;
            const isSelected = selectedId === item.id;
            const endDateLabel = formatSocialMediaImageEndDate(item.endDate);
            return (
              <button
                key={item.id}
                type="button"
                className={styles.itemOuter}
                onClick={() => setSelectedId(item.id)}
              >
                <span
                  className={
                    isCircle
                      ? `${styles.item} ${isSelected ? styles.selected : ''} ${isAlreadyChosen ? styles.itemAlreadyChosen : ''}`
                      : `${styles.item} ${styles.itemRectangle} ${isSelected ? styles.selected : ''} ${isAlreadyChosen ? styles.itemAlreadyChosen : ''}`
                  }
                >
                  <img
                    src={item.imageUrl}
                    alt=""
                    className={styles.itemImage}
                  />
                </span>
                {endDateLabel ? (
                  <span
                    className={classnames(
                      styles.itemEndDate,
                      isCircle
                        ? styles.itemEndDateAvatar
                        : styles.itemEndDateBackground
                    )}
                  >
                    {trans('userspace.edit-card.picker.end-date', {
                      date: endDateLabel
                    })}
                  </span>
                ) : null}
              </button>
            );
          })
        )}
      </div>
      {showSponsoredDisclaimer ? (
        <p className={styles.sponsoredDisclaimer} role="note">
          {trans('userspace.edit-card.picker.sponsored.disclaimer')}
        </p>
      ) : null}
      <div className={styles.footer}>
        <Button theme="default" type="full" size="sm" onClick={handleCancel}>
          {trans('generic.cancel')}
        </Button>
        <Button
          theme="inverse"
          type="full"
          size="sm"
          onClick={handleValidate}
          disabled={!selectedItem || saving}
        >
          {saving
            ? trans('userprofile.edit-card.picker.saving')
            : trans('userspace.validate')}
        </Button>
      </div>
    </ReactModal>
  );
};

export default buildReactDomModalModule<
  ProfileCardPickerModalParams | null | undefined
>(
  EDIT_PROFILE_CARD_PICKER_MODAL,
  (params, closeModal, _askBeforeClosing, onCloseParams) => (
    <ProfileCardPickerModal
      closeModal={closeModal}
      onCloseParams={onCloseParams}
      params={params}
    />
  )
);
